/**
 * Phase 34 Session 34.3 — Telemetry batch queue.
 *
 * Pure FIFO buffer with size + time triggers. Persistence is OPTIONAL via
 * an injected `PersistenceAdapter` — the dashboard skips persistence (page
 * reloads drop in-flight events; acceptable since browser tabs typically
 * stay open through the 5s flush window) while the POS injects an
 * `AsyncStorageAdapter` so an offline shift survives device reload.
 *
 * The queue is structurally agnostic to event shape — both page-views and
 * UI-events flow through it via per-channel `Queue` instances.
 */

/** Optional persistence layer. Mirrors pos-core's `StorageAdapter` shape
 *  so consumers can reuse existing implementations. */
export interface PersistenceAdapter<T> {
  load(): Promise<T[]> | T[];
  save(items: T[]): Promise<void> | void;
  clear(): Promise<void> | void;
}

export interface QueueOptions<T> {
  /** Maximum buffered items before an auto-flush is forced. Default 50. */
  sizeLimit?: number;
  /** Hard cap on buffered items — beyond this the queue silently drops the
   *  OLDEST entries (defense against runaway producers). Default 5000. */
  hardCap?: number;
  /** Optional persistence layer. */
  persistence?: PersistenceAdapter<T>;
}

const DEFAULT_SIZE_LIMIT = 50;
const DEFAULT_HARD_CAP = 5000;

export class TelemetryQueue<T> {
  private buffer: T[] = [];
  private readonly sizeLimit: number;
  private readonly hardCap: number;
  private readonly persistence?: PersistenceAdapter<T>;
  private hydrated = false;

  constructor(opts: QueueOptions<T> = {}) {
    this.sizeLimit = Math.max(1, opts.sizeLimit ?? DEFAULT_SIZE_LIMIT);
    // Trust caller-supplied hardCap verbatim (clamped at 1) — sizeLimit > hardCap
    // is a legit edge case (e.g. tight test fixtures) where every enqueue past
    // hardCap evicts the oldest, and no size-trigger fires until the buffer
    // would exceed sizeLimit (which it can't, because hardCap clamps it).
    this.hardCap = Math.max(1, opts.hardCap ?? DEFAULT_HARD_CAP);
    this.persistence = opts.persistence;
  }

  /**
   * Hydrate from the persistence layer (no-op when no adapter set). Idempotent
   * — multiple boots safely no-op after the first one. Adapter errors are
   * silently swallowed so a corrupt persisted blob can't brick the SDK.
   */
  async hydrate(): Promise<void> {
    if (this.hydrated || !this.persistence) {
      this.hydrated = true;
      return;
    }
    try {
      const items = await this.persistence.load();
      if (Array.isArray(items)) {
        // Tolerate a corrupt persisted blob by truncating to the hard cap.
        this.buffer = items.slice(-this.hardCap);
      }
    } catch {
      // Swallow — better to start clean than to crash the consumer.
    }
    this.hydrated = true;
  }

  /** Append a single item. Returns true when the size-trigger fires (caller
   *  should flush). */
  enqueue(item: T): boolean {
    this.buffer.push(item);
    if (this.buffer.length > this.hardCap) {
      // Drop the oldest entries — newest data is more useful for reporting.
      this.buffer = this.buffer.slice(-this.hardCap);
    }
    void this.persist();
    return this.buffer.length >= this.sizeLimit;
  }

  /** Append multiple items at once. Same return semantics as `enqueue`. */
  enqueueMany(items: T[]): boolean {
    if (items.length === 0) return false;
    this.buffer.push(...items);
    if (this.buffer.length > this.hardCap) {
      this.buffer = this.buffer.slice(-this.hardCap);
    }
    void this.persist();
    return this.buffer.length >= this.sizeLimit;
  }

  /** Drain the buffer + return its contents. */
  drain(): T[] {
    const out = this.buffer;
    this.buffer = [];
    void this.persist();
    return out;
  }

  /** Re-insert items at the head of the buffer (used when a flush attempt
   *  fails — keeps order roughly intact for the next try). */
  prepend(items: T[]): void {
    if (items.length === 0) return;
    this.buffer = [...items, ...this.buffer];
    if (this.buffer.length > this.hardCap) {
      this.buffer = this.buffer.slice(-this.hardCap);
    }
    void this.persist();
  }

  size(): number {
    return this.buffer.length;
  }

  /** Test/debug view of the current buffer (returns a copy). */
  snapshot(): T[] {
    return [...this.buffer];
  }

  private async persist(): Promise<void> {
    if (!this.persistence) return;
    try {
      await this.persistence.save(this.buffer);
    } catch {
      // Swallow — consumer's main flow shouldn't crash because storage
      // is full or permission was denied.
    }
  }
}
